/**
 * Team Management Tab Component
 *
 * Manages project team members with roles and activity tracking.
 *
 * Features:
 * - List team members with role and activity status
 * - Add new team members from company users
 * - Update team member roles (inline dropdown)
 * - Remove team members with confirmation
 * - Filter by role
 * - Search by name/email
 *
 * Follows: .equilateral-standards/frontend-development/frontend_standards.md
 * Follows: .equilateral-standards/frontend-development/tim_combo_frontend_auth_pattern.md
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Modal,
  Label,
  TextInput,
  Select,
  Badge,
  Spinner,
  Alert,
} from 'flowbite-react';
import { HiPlus, HiTrash, HiSearch, HiFilter } from 'react-icons/hi';
import { useStatus } from '../contexts/StatusProvider';
import { getAllUsers, User, isSuccessResponse } from '../services/api/users';
import {
  getProjectTeamMembers,
  addProjectTeamMember,
  updateProjectTeamMember,
  removeProjectTeamMember,
  ProjectTeamMember,
  AddTeamMemberRequest,
} from '../api/projects';

interface TeamManagementTabProps {
  clientId: string;
  companyId: string;
  projectId: string;
}

const getRoleBadgeColor = (role: string): string => {
  switch (role) {
    case 'Lead':
      return 'purple';
    case 'Developer':
      return 'blue';
    case 'Reviewer':
      return 'green';
    case 'Observer':
      return 'gray';
    default:
      return 'gray';
  }
};

const getActivityBadgeColor = (status: string | undefined): string => {
  switch (status) {
    case 'Active':
      return 'success';
    case 'Recently Active':
      return 'warning';
    case 'Inactive':
      return 'failure';
    case 'Never Active':
    default:
      return 'gray';
  }
};

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return 'Never';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return 'Invalid date';
  }
};

export const TeamManagementTab: React.FC<TeamManagementTabProps> = ({
  clientId,
  companyId,
  projectId,
}) => {
  // Tim-Combo Pattern: Use StatusProvider for auth state
  const { Auth_Status } = useStatus();

  // State management
  const [teamMembers, setTeamMembers] = useState<ProjectTeamMember[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<ProjectTeamMember | null>(null);

  // Form state
  const [formData, setFormData] = useState<AddTeamMemberRequest>({
    Client_ID: clientId,
    Company_ID: companyId,
    Email_Address: '',
    Role: 'Developer',
  });

  // Filter state
  const [filters, setFilters] = useState({
    search: '',
    role: '',
  });

  // Load team members
  const loadTeamMembers = useCallback(async () => {
    if (!clientId || !companyId || !projectId) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await getProjectTeamMembers(
        clientId,
        companyId,
        projectId,
        filters.role || undefined
      );

      if (response.success) {
        setTeamMembers(response.data.Records);
      } else {
        setError(response.message || 'Failed to load team members');
      }
    } catch (err) {
      console.error('[TeamManagementTab] Error loading team members:', err);
      setError(err instanceof Error ? err.message : 'Failed to load team members');
    } finally {
      setIsLoading(false);
    }
  }, [clientId, companyId, projectId, filters.role]);

  // Load available users
  const loadAvailableUsers = useCallback(async () => {
    try {
      const response = await getAllUsers();

      if (isSuccessResponse(response)) {
        // Filter to only show active users from the same company
        const companyUsers = response.data.Records.filter(
          (user) => user.User_Status.toLowerCase() === 'active'
        );
        setAvailableUsers(companyUsers);
      }
    } catch (err) {
      console.error('[TeamManagementTab] Error loading users:', err);
    }
  }, []);

  // Tim-Combo Pattern: useEffect for side effects (data fetching)
  useEffect(() => {
    if (Auth_Status) {
      loadTeamMembers();
    }
  }, [Auth_Status, loadTeamMembers]);

  // Handle add team member
  const handleAddMember = async () => {
    if (!formData.Email_Address) {
      setError('Please select a user');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      const response = await addProjectTeamMember(projectId, formData);

      if (response.success) {
        setSuccessMessage(`Team member added successfully`);
        setShowAddModal(false);
        resetForm();
        await loadTeamMembers();

        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(response.message || 'Failed to add team member');
      }
    } catch (err) {
      console.error('[TeamManagementTab] Error adding team member:', err);
      setError(err instanceof Error ? err.message : 'Failed to add team member');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle update team member role
  const handleUpdateRole = async (member: ProjectTeamMember, newRole: string) => {
    if (member.Role === newRole) return;

    try {
      setError(null);

      const response = await updateProjectTeamMember(projectId, member.Email_Address, {
        Client_ID: clientId,
        Company_ID: companyId,
        Role: newRole as 'Developer' | 'Lead' | 'Reviewer' | 'Observer',
      });

      if (response.success) {
        setSuccessMessage(`Role updated to ${newRole}`);
        await loadTeamMembers();

        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(response.message || 'Failed to update role');
      }
    } catch (err) {
      console.error('[TeamManagementTab] Error updating role:', err);
      setError(err instanceof Error ? err.message : 'Failed to update role');
    }
  };

  // Handle remove team member
  const handleRemoveMember = async () => {
    if (!selectedMember) return;

    try {
      setIsSaving(true);
      setError(null);

      const response = await removeProjectTeamMember(
        clientId,
        companyId,
        projectId,
        selectedMember.Email_Address
      );

      if (response.success) {
        setSuccessMessage(`Team member removed successfully`);
        setShowRemoveModal(false);
        setSelectedMember(null);
        await loadTeamMembers();

        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(response.message || 'Failed to remove team member');
      }
    } catch (err) {
      console.error('[TeamManagementTab] Error removing team member:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove team member');
    } finally {
      setIsSaving(false);
    }
  };

  // Open add modal
  const openAddModal = async () => {
    await loadAvailableUsers();
    resetForm();
    setShowAddModal(true);
  };

  // Open remove modal
  const openRemoveModal = (member: ProjectTeamMember) => {
    setSelectedMember(member);
    setShowRemoveModal(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      Client_ID: clientId,
      Company_ID: companyId,
      Email_Address: '',
      Role: 'Developer',
    });
    setError(null);
  };

  // Filter team members by search
  const filteredTeamMembers = teamMembers.filter((member) => {
    if (!filters.search) return true;
    const searchLower = filters.search.toLowerCase();
    return (
      member.Email_Address.toLowerCase().includes(searchLower) ||
      member.User_Display_Name?.toLowerCase().includes(searchLower) ||
      member.First_Name?.toLowerCase().includes(searchLower) ||
      member.Last_Name?.toLowerCase().includes(searchLower)
    );
  });

  // Get users not already on team
  const availableUsersNotOnTeam = availableUsers.filter(
    (user) => !teamMembers.some((member) => member.Email_Address === user.Email_Address)
  );

  return (
    <div className="space-y-4">
      {/* Error/Success Messages */}
      {error && (
        <Alert color="failure" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert color="success" onDismiss={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Filters and Actions */}
      <div className="flex flex-col md:flex-row gap-4 items-end">
        <div className="flex-1">
          <Label htmlFor="search">Search Team Members</Label>
          <TextInput
            id="search"
            icon={HiSearch}
            placeholder="Search by name or email..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
        </div>

        <div className="w-full md:w-48">
          <Label htmlFor="roleFilter">Filter by Role</Label>
          <Select
            id="roleFilter"
            icon={HiFilter}
            value={filters.role}
            onChange={(e) => setFilters({ ...filters, role: e.target.value })}
          >
            <option value="">All Roles</option>
            <option value="Lead">Lead</option>
            <option value="Developer">Developer</option>
            <option value="Reviewer">Reviewer</option>
            <option value="Observer">Observer</option>
          </Select>
        </div>

        <Button color="blue" onClick={openAddModal}>
          <HiPlus className="mr-2 h-5 w-5" />
          Add Team Member
        </Button>
      </div>

      {/* Team Members Table */}
      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <Spinner size="xl" />
        </div>
      ) : filteredTeamMembers.length === 0 ? (
        <div className="text-center text-gray-500 dark:text-gray-400 py-12">
          {teamMembers.length === 0
            ? 'No team members assigned to this project yet'
            : 'No team members match your search criteria'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table hoverable>
            <Table.Head>
              <Table.HeadCell>Name</Table.HeadCell>
              <Table.HeadCell>Email</Table.HeadCell>
              <Table.HeadCell>Role</Table.HeadCell>
              <Table.HeadCell>Added</Table.HeadCell>
              <Table.HeadCell>Last Activity</Table.HeadCell>
              <Table.HeadCell>Status</Table.HeadCell>
              <Table.HeadCell>Actions</Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {filteredTeamMembers.map((member) => (
                <Table.Row
                  key={member.Email_Address}
                  className="bg-white dark:border-gray-700 dark:bg-gray-800"
                >
                  <Table.Cell className="font-medium text-gray-900 dark:text-white">
                    {member.User_Display_Name ||
                      `${member.First_Name || ''} ${member.Last_Name || ''}`.trim() ||
                      '-'}
                  </Table.Cell>
                  <Table.Cell>{member.Email_Address}</Table.Cell>
                  <Table.Cell>
                    <Select
                      sizing="sm"
                      value={member.Role}
                      onChange={(e) => handleUpdateRole(member, e.target.value)}
                      className="w-32"
                    >
                      <option value="Lead">Lead</option>
                      <option value="Developer">Developer</option>
                      <option value="Reviewer">Reviewer</option>
                      <option value="Observer">Observer</option>
                    </Select>
                  </Table.Cell>
                  <Table.Cell>
                    <div className="text-sm">
                      {formatDate(member.Added_At)}
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        by {member.Added_By}
                      </div>
                    </div>
                  </Table.Cell>
                  <Table.Cell>{formatDate(member.Last_Activity)}</Table.Cell>
                  <Table.Cell>
                    <Badge color={getActivityBadgeColor(member.Activity_Status)}>
                      {member.Activity_Status || 'Unknown'}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <Button
                      size="xs"
                      color="failure"
                      onClick={() => openRemoveModal(member)}
                      title="Remove from project"
                    >
                      <HiTrash className="h-4 w-4" />
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="text-center">
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {teamMembers.length}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">Total Members</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-purple-600">
            {teamMembers.filter((m) => m.Role === 'Lead').length}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">Leads</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-blue-600">
            {teamMembers.filter((m) => m.Role === 'Developer').length}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">Developers</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-green-600">
            {teamMembers.filter((m) => m.Role === 'Reviewer').length}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">Reviewers</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-gray-600">
            {teamMembers.filter((m) => m.Role === 'Observer').length}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">Observers</p>
        </div>
      </div>

      {/* Add Team Member Modal */}
      <Modal show={showAddModal} onClose={() => setShowAddModal(false)}>
        <Modal.Header>Add Team Member</Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <div>
              <Label htmlFor="userSelect">Select User *</Label>
              <Select
                id="userSelect"
                value={formData.Email_Address}
                onChange={(e) =>
                  setFormData({ ...formData, Email_Address: e.target.value })
                }
                required
              >
                <option value="">-- Select a user --</option>
                {availableUsersNotOnTeam.map((user) => (
                  <option key={user.Email_Address} value={user.Email_Address}>
                    {user.User_Display_Name || user.Email_Address} ({user.Email_Address})
                  </option>
                ))}
              </Select>
              {availableUsersNotOnTeam.length === 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  All company users are already on this project team
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="roleSelect">Role *</Label>
              <Select
                id="roleSelect"
                value={formData.Role}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    Role: e.target.value as 'Developer' | 'Lead' | 'Reviewer' | 'Observer',
                  })
                }
                required
              >
                <option value="Developer">Developer - Write code and implement features</option>
                <option value="Lead">Lead - Manage project and team direction</option>
                <option value="Reviewer">Reviewer - Review code and approve changes</option>
                <option value="Observer">Observer - View-only access</option>
              </Select>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={handleAddMember}
            disabled={!formData.Email_Address || isSaving}
            isProcessing={isSaving}
          >
            Add Member
          </Button>
          <Button color="gray" onClick={() => setShowAddModal(false)} disabled={isSaving}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Remove Confirmation Modal */}
      <Modal show={showRemoveModal} onClose={() => setShowRemoveModal(false)} size="md">
        <Modal.Header>Remove Team Member</Modal.Header>
        <Modal.Body>
          <p className="text-gray-700 dark:text-gray-300">
            Are you sure you want to remove{' '}
            <strong>
              {selectedMember?.User_Display_Name || selectedMember?.Email_Address}
            </strong>{' '}
            from this project team?
          </p>
          <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-2">
            This will remove their {selectedMember?.Role} role and project access. They can be
            re-added later if needed.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            color="failure"
            onClick={handleRemoveMember}
            disabled={isSaving}
            isProcessing={isSaving}
          >
            Remove
          </Button>
          <Button color="gray" onClick={() => setShowRemoveModal(false)} disabled={isSaving}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TeamManagementTab;
